import { Chip, Avatar, Tooltip } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import { useRegistry } from "../context/RegistryContext";
import type { LogoKind } from "../api/types";
import { logoUrl } from "../api/logo";

export type EntityType = LogoKind | "expenseType"; // Accept camelCase expenseType

export type EntityChipProps = {
  type: EntityType;
  name: string;
  variant?: "filled" | "outlined";
  showLogo?: boolean;
  iconOnly?: boolean;
  size?: "small" | "medium";
  onClick?: () => void;
  onDelete?: () => void;
};

export const EntityChip = ({
  type,
  name,
  variant = "filled",
  showLogo = true,
  iconOnly = false,
  size = "medium",
  onClick,
  onDelete,
}: EntityChipProps) => {
  // A chip names what a record references, so hidden entities still resolve
  // — hiding keeps them out of pickers, not out of the rows that use them.
  const { getSource, getGroup, getCategory, getExpenseType, getPayee } =
    useRegistry().withHidden;

  let entity;
  let logoKind: LogoKind = type as LogoKind;

  if (type === "source") entity = getSource(name);
  else if (type === "group") entity = getGroup(name);
  else if (type === "category") entity = getCategory(name);
  else if (type === "payee") entity = getPayee(name);
  else if (type === "expenseType" || type === "expense-type") {
    entity = getExpenseType(name);
    logoKind = "expense-type";
  }

  if (!entity) {
    // Fallback if entity not found
    return <Chip label={name} size={size} variant={variant} />;
  }

  const avatar = showLogo ? (
    <Avatar
      alt={entity.name}
      src={logoUrl(logoKind, entity)}
      sx={{
        width: 24,
        height: 24,
        bgcolor: entity.colorBg || undefined,
        color: entity.colorFg || undefined,
        fontSize: "0.75rem",
        border: "1px solid #ccc",
      }}
    />
  ) : undefined;

  // Cast to specific MUI Chip style props to avoid 'any'
  const baseStyle: Record<string, string | number | undefined> = {
    fontWeight: entity.style === "bold" ? "bold" : undefined,
    fontStyle: entity.style === "italic" ? "italic" : undefined,
  };

  if (variant === "filled") {
    baseStyle.bgcolor = entity.colorBg || undefined;
    baseStyle.color = entity.colorFg || undefined;
  } else {
    // Outlined
    baseStyle.borderColor = entity.colorBg || undefined;
    baseStyle.color = entity.colorBg || undefined; // Use bg color for text
    // Ensure background is transparent to avoid "invisible text" issue
    baseStyle.bgcolor = "transparent";
  }

  // Override height if needed for small size consistency
  if (size === "small") {
    baseStyle.height = 24;
  }

  if (iconOnly && showLogo) {
    return (
      <Tooltip title={entity.name}>
        <Avatar
          alt={entity.name}
          src={logoUrl(logoKind, entity)}
          onClick={onClick}
          sx={{
            width: size === "small" ? 24 : 32,
            height: size === "small" ? 24 : 32,
            bgcolor: entity.colorBg || undefined,
            color: entity.colorFg || undefined,
            fontSize: size === "small" ? "0.75rem" : "1rem",
            border: "1px solid #ccc",
            cursor: onClick ? "pointer" : "default",
          }}
        />
      </Tooltip>
    );
  }

  return (
    <Tooltip title={entity.name}>
      <Chip
        avatar={avatar}
        label={entity.name}
        variant={variant}
        size={size}
        onClick={onClick}
        onDelete={onDelete}
        sx={baseStyle as SxProps<Theme>}
      />
    </Tooltip>
  );
};
